<template>
  <AppWithLoading
    :fetch="fetchCandidatesWithFilters"
    class="loading-container"
  >
    <VRow class="mt-9">
      <PageTitle
        tag="h1"
        no-border
        class="candidates__title text-left ml-2"
      >
        {{ $t('name') }}
      </PageTitle>
    </VRow>
    <VRow class="main-block">
      <div class="candidates-table__header ml-1">
        <div class="candidates-table__textfield">
          <VTextField
            v-model="search"
            :label="$t('search')"
            class="ml-1"
            outlined
            @input="doSearch"
          />
          <img :src="require('@/assets/img/candidates/search.svg')" alt="search" />
        </div>
        <div @click.stop="openFilters()">
          <div v-if="!filtersActive" class="candidates-table__filters-btn">
            <img :src="require('@/assets/img/candidates/filters.svg')" alt="filters" />
          </div>
          <div v-else class="candidates-table__filters-btn-active">
            <img :src="require('@/assets/img/candidates/filters2.svg')" alt="filters" />
          </div>
        </div>
        <button
          :disabled="!haveFilters"
          @click="resetFilters"
          class="candidates-table__filters-reset"
        >
          {{ $t('filtersReset') }}
        </button>

        <VBtn
          color="next"
          class="ml-3"
          @click="exportCandidatesAll()"
        >
          {{ $t('exportCandidates') }}
        </VBtn>
      </div>

      <VCol v-if="!loading" class="table">
        <table v-if="totalCandidates">
          <thead>
            <tr>
              <th
                v-for="(header, index) in headers"
                :key="index"
                :class="{ 'th--sortable': header.sortable }"
                @click="onSortHeaderClick(header, $event)"
              >
                <div
                  v-if="header.sortable"
                  class="th-wrap th-wrap--sortable"
                >
                  <div
                    v-if="header.value === 'cName'"
                    class="th-wrap__checkboxes"
                  >
                    <VCheckbox
                      @change="selectUsers"
                      v-model="selectedAllUsers"
                    />
                    <img
                      :src="require('@/assets/img/candidates/arrow-down.svg')"
                      alt="arrow-down"
                      :class="[ isToggledCheckboxUsers ? 'up' : 'down' ]"
                    />
                  </div>
                  <span
                    :class="[ header.color ? 'black-color' : 'default-color' ]"
                  >
                    {{ header.text }}
                  </span>
                  <VIcon
                    x-small
                    :class="[ header.isToggled ? 'up' : 'down', header.color ? 'black-color' : 'default-color']"
                  >
                    mdi-arrow-down
                  </VIcon>
                </div>
                <div v-else>
                  {{ header.text }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="candidate in candidates"
              :key="candidate.id"
              @input="updateSelected"
            >
              <td class="td-wrap__name">
                <VCheckbox :value="candidate.id" v-model="selectedUsers" />
                <a @click="openCandidate(candidate.id, candidate.vacancy.id)" href="#">
                  {{ `${candidate.firstName} ${candidate.lastName}` }}
                </a>
              </td>
              <td class="td-wrap__buttons">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  <button
                    v-if="candidate.resume"
                    class="candidateActionButton candidateActionButton__resume"
                    @click="downloadResume(candidate)"
                  >
                    <img :src="require('@/assets/img/candidates/resume.svg')" alt="resume" />
                  </button>
                  <button
                    v-if="candidate.interviewedAt"
                    class="candidateActionButton candidateActionButton__card"
                    @click="openCandidate(candidate.id, candidate.vacancy.id)"
                  >
                    <img :src="require('@/assets/img/candidates/card.svg')" alt="card" />
                  </button>
                </div>
              </td>
              <td class="td-wrap__email">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  {{ candidate.email }}
                </div>
              </td>
              <td class="td-wrap__phone">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  {{ candidate.phone }}
                </div>
              </td>
              <td class="td-wrap__date">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  {{ formattedCreatedAt(candidate) }}
                </div>
              </td>
              <td class="td-wrap__vacancy">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  <RouterLink :to="`/vacancy/${candidate.vacancy.id}/`">
                    {{ candidate.vacancy.title }}
                  </RouterLink>
                </div>
              </td>
              <td class="td-wrap__status">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  {{ $t(setCandidateStatus(candidate)) }}
                </div>
              </td>
              <td class="td-wrap__invite" v-if="isMobile">
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  <img
                    :src="require('@/assets/img/candidates/invite.svg')"
                    alt="invite"
                    class="invite-candidate-icon"
                  />
                  <div
                    @click="setCandidateName(candidate)"
                    class="td-wrap__invite_text"
                  >
                    {{ $t('inviteForAJob') }}
                  </div>
                </div>
              </td>
              <td v-else>
                <div
                  :class="{ 'candidate-row__blurred': isQuotaExceededForRow(candidate) }"
                  class="td-wrap__blur-wrapper"
                >
                  <Tooltip
                    :text="$t('copyCandidateTooltip')"
                  >
                    <div
                      @click="setCandidateName(candidate)"
                      class="td-wrap__invite_text"
                    >
                      {{ $t('invite') }}
                    </div>
                  </Tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="totalCandidates === 0" class="no-candidates">
          <div>{{ $t('noCandidates') }}</div>
          <div @click="drawer = true" class="no-candidates__open-filters">
            {{ $t('openFilters') }}
          </div>
        </div>
      </VCol>
      <VCol v-if="loading">
        <Preloader />
      </VCol>

      <div class="candidates-table__footer">
        <template v-if="totalCandidates > 5">
          <div class="candidates-table__pagination">
            <div
              v-if="page !== 1"
              @click="changePagePrev"
              class="candidates-table__pagination_arrow"
            >
              <img :src="require('@/assets/img/candidates/arrow-prev.svg')" alt="arrow-prev" />
            </div>
            <ul>
              <li
                v-for="currentPage in metaData.lastPage"
                :key="currentPage"
                :class="{ isActivePage: page === currentPage }"
              >
                <template
                  v-if="setVisiblePage(currentPage)"
                >
                  <div
                    class="paginationItem"
                    @input="loadPage($event)"
                    @click="changePage(currentPage)"
                  >
                    {{ currentPage }}
                  </div>
                </template>

                <template>
                  <div
                    v-if="setVisibleDots(currentPage)"
                  >
                    ...
                  </div>
                </template>
              </li>
            </ul>
            <div
              v-if="page !== metaData.lastPage"
              @click="changePageNext"
              class="candidates-table__pagination_arrow"
            >
              <img :src="require('@/assets/img/candidates/arrow-next.svg')" alt="arrow-next" />
            </div>
          </div>
        </template>

        <div v-if="selectedUsers.length" class="candidates-table__selected">
          <span>{{ $t('selected') }}: {{ selectedUsers.length }}</span>
          <VBtn v-if="isMobile" @click="openInviteMenu()" color="#1FC57F">
            {{ $t('invite') }}
          </VBtn>
          <VBtn v-else @click="openInviteMenu()" color="#1FC57F">
            {{ $t('inviteForAJob') }}
          </VBtn>
          <button @click="closeInvitations()">
            <IconClose />
          </button>
        </div>

        <div />
      </div>
    </VRow>
    <CandidateCard
      v-if="vacancySelected && candidateSelectedId"
      :vacancy="vacancySelected"
      :candidate-id="candidateSelectedId"
      :show-neighbours="false"
      :push-after-close="false"
      @close="closeResume()"
    />
    <VSheet
      class="overflow-hidden"
      absolute
      right
    >
      <VNavigationDrawer
        v-model="drawer"
        absolute
        right
        width="500"
        temporary
      >
        <div class="candidates-filters__wrap">
          <div class="candidates-filters__wrap_header">
            <PageTitle
              tag="h1"
              no-border
              class="candidates-filters__title text-left"
            >
              {{ $t('filters') }}
            </PageTitle>
            <VBtn
              class="v-btn-close"
              icon
              small
              @click="drawer = false"
            >
              <IconClose />
            </VBtn>
          </div>
          <div class="candidates-filters__wrap_date-title">
            {{ $t('dateAddCandidate') }}
          </div>
          <div class="candidates-filters__wrap_date-range">
            <div
              class="candidates-table__textfield"
              style="width: 100%"
            >
              <VTextField
                v-model="filtersStartDate"
                type="text"
                :label="localeLabelStart"
                @input="formatDateOnChange()"
                onkeypress="return /[0-9]/i.test(event.key)"
                maxlength="10"
                outlined
                autocomplete="rutjfkde"
              />
              <img
                :src="require('@/assets/img/candidates/calendar.svg')"
                alt="calendar"
                @click="openStartCalendar()"
              />
            </div>
            <VDatePicker
              v-if="dateStartPickerActive"
              v-model="filtersStartDate"
              no-title
              scrollable
              class="date-picker__start"
              @change="setStartDate()"
            >
              <VBtn
                class="v-btn-close"
                icon
                x-small
                @click="dateStartPickerActive = false"
              >
                <IconClose />
              </VBtn>
            </VDatePicker>
            <hr>
            <div
              class="candidates-table__textfield"
              style="width: 100%"
            >
              <VTextField
                v-model="filtersEndDate"
                type="text"
                :label="localeLabelEnd"
                @input="formatDateOnChange()"
                onkeypress="return /[0-9]/i.test(event.key)"
                maxlength="10"
                outlined
                autocomplete="rutjfkde"
              />
              <img
                :src="require('@/assets/img/candidates/calendar2.svg')"
                alt="calendar2"
                @click="openEndCalendar()"
              />
            </div>
            <VDatePicker
              v-if="dateEndPickerActive"
              v-model="filtersEndDate"
              no-title
              scrollable
              class="date-picker__end"
              @change="setEndDate()"
            >
              <VBtn
                class="v-btn-close"
                icon
                x-small
                @click="dateEndPickerActive = false"
              >
                <IconClose />
              </VBtn>
            </VDatePicker>
          </div>
          <div v-if="compareDate" class="candidates-filters__wrap_date-range-error-message">
            {{ $t('dateRangeErrorMessage') }}
          </div>
          <VExpansionPanels
            v-model="panel"
            multiple
          >
            <VExpansionPanel>
              <VExpansion-panel-header>{{ $t('vacancy') }}</VExpansion-panel-header>
              <VExpansion-panel-content>
                <div class="candidates-table__textfield" style="width: 100%">
                  <VTextField
                    v-model="searchVacancy"
                    :label="$t('search')"
                    outlined
                  />
                  <img
                    :src="require('@/assets/img/candidates/search-calendar.svg')"
                    alt="search-calendar"
                  />
                </div>

                <div class="candidates-table__vacancies-list">
                  <div
                    v-for="vacancy in searchVacancyHandler"
                    :key="vacancy.id"
                  >
                    <VCheckbox
                      v-if="vacancy.status !== vacancyStatus.DRAFT"
                      v-model="selectedVacancyName"
                      :value="vacancy.id"
                      :label="vacancy.title"
                    />
                  </div>
                </div>

                <div v-if="searchVacancy && !searchVacancyHandler.length">
                  {{ $t('noVacancies') }}
                </div>
              </VExpansion-panel-content>
            </VExpansionPanel>

            <VExpansionPanel>
              <VExpansion-panel-header>{{ $t('status') }}</VExpansion-panel-header>
              <VExpansion-panel-content>
                <div class="candidates-table__vacancies-list">
                  <div
                    v-for="status in statuses"
                    :key="status.id"
                  >
                    <VCheckbox
                      v-model="selectedStatuses"
                      :value="status.status"
                      :label="$t(status.name)"
                    />
                  </div>
                </div>
              </VExpansion-panel-content>
            </VExpansionPanel>
          </VExpansionPanels>
          <div class="btn-show-block">
            <VBtn
              @click="resetFilters"
              color="#EEEEEE"
              width="100%"
              class="candidates-table__filters-reset-mob black--text"
            >
              {{ $t('filtersReset') }}
            </VBtn>
            <VBtn
              @click="showResultsFilters"
              color="#1FC57F"
              width="100%"
              :disabled="nothingSelected || compareDate"
            >
              {{ $t('showResults') }}
            </VBtn>
          </div>
        </div>
      </VNavigationDrawer>
    </VSheet>
    <VSheet
      class="overflow-hidden"
      absolute
      right
    >
      <VNavigationDrawer
        v-model="isInviteCandidates"
        absolute
        right
        width="500"
        temporary
      >
        <div class="candidates-filters__wrap">
          <div class="candidates-filters__wrap_header">
            <PageTitle
              tag="h1"
              no-border
              class="candidates-filters__title text-left"
            >
              <span>{{ titleInvitations }}</span>
            </PageTitle>
            <VBtn
              class="v-btn-close"
              icon
              small
              @click="isInviteCandidates = false"
            >
              <IconClose />
            </VBtn>
          </div>
          <VExpansionPanels
            v-model="panel"
            multiple
          >
            <VExpansionPanel>
              <VExpansion-panel-header>{{ $t('selectVacancy') }}</VExpansion-panel-header>
              <VExpansion-panel-content>
                <div class="candidates-table__textfield" style="width: 100%">
                  <VTextField
                    v-model="searchVacancy"
                    :label="$t('search')"
                    outlined
                  />
                  <img
                    :src="require('@/assets/img/candidates/search-vacancy.svg')"
                    alt="search-vacancy"
                  />
                </div>
                <div class="candidates-table__vacancies-list">
                  <VRadioGroup v-model="selectedVacancyRadio">
                    <div
                      v-for="vacancy in searchVacancyHandler"
                      :key="vacancy.id"
                    >
                      <VRadio
                        v-if="vacancy.status !== vacancyStatus.DRAFT"
                        :disabled="vacancy.status === vacancyStatus.ARCHIVE"
                        :label="vacancy.title"
                        :value="vacancy.id"
                        class="mb-2"
                      />
                    </div>
                  </VRadioGroup>
                </div>

                <div v-if="searchVacancy && !searchVacancyHandler.length">
                  {{ $t('noVacancies') }}
                </div>
              </VExpansion-panel-content>
            </VExpansionPanel>
          </VExpansionPanels>

          <div class="candidates-filters__wrap_status-title mt-5">
            {{ $t('status') }}
          </div>

          <VSelect
            :items="statuses"
            v-model="selectedStatus"
            name="select"
            item-text="name"
            item-value="status"
          />

          <VCheckbox
            v-model="withInvitation"
            class="mt-2"
            :label="$t('inviteToVirtualInterview')"
            type="checkbox"
          />

          <QuotaResolver v-if="withInvitation" v-slot:default="{quota}" class="notify">
            <VRadioGroup v-model="notifyBy">
              <div @click="setNotifyTypeValue">
                <VRadio
                  v-for="notifyType in notifyTypes"
                  class="notify__item"
                  :key="notifyType.value"
                  :value="notifyType.value"
                >
                  <template #label>
                    {{ notifyType.text }}
                    <span v-if="notifyType.value !== 'Email' && notifyType" class="notify__rest-amount">
                      ({{ notifyType.quotaCode }})
                      <span
                        class="notify__rest-hint"
                        :class="{'warning-SMSes': ticketsMoreThanSMSes, 'warning-calls': ticketsMoreThanCalls}"
                      >
                        {{ $tc(`notifyBy${notifyType.value}Tooltip`, notifyType.quotaCode) + ". " }}
                        <router-link to="/settings/payment/plans" target="_blank">
                          {{ $t('changeTariff') }}
                        </router-link>
                      </span>
                    </span>
                  </template>
                </VRadio>
              </div>
            </VRadioGroup>
            <template v-if="notifyBy !== '' && phoneCount > quota[getQuotaCodeByNotify(notifyBy)] && quota[getQuotaCodeByNotify(notifyBy)] !== 0">
              <VAlert
                border="bottom"
                colored-border
                type="warning"
                elevation="2"
              >
                {{ getQuotaAlertText(quota[getQuotaCodeByNotify(notifyBy)]) }}
              </VAlert>
            </template>
          </QuotaResolver>

          <div class="btn-show-block">
            <VBtn
              @click="inviteCandidate()"
              color="#1FC57F"
              width="100%"
              :disabled="!selectedVacancyRadio"
            >
              {{ $t('invite') }}
            </VBtn>
          </div>
        </div>
      </VNavigationDrawer>
    </VSheet>
  </AppWithLoading>
</template>

<script>
import {
  ref, computed, watch, toRefs, onMounted,
} from '@vue/composition-api';
import {
  candidateNotificationTypes, quotaItemTypes, candidateStatus, vacancyStatus,
} from '@/enums';
import AppWithLoading from '@/components/AppWithLoading';
import useCandidates from '@/compositions/useCandidates';
import useVacancies from '@/compositions/useVacancies';
import CandidateCard from '@/views/Candidate.vue';
import { useI18n } from 'vue-i18n-composable';
import Preloader from '@/components/Preloader';
import useDateFormatter from '@/compositions/useDateFormatter';
import PageTitle from '@/components/PageTitle';
import IconClose from '@/components/SvgIcons/Close.vue';
import Tooltip from '@/components/Candidate/CandidatesTableRowTooltip.vue';
import QuotaResolver from '@/components/Payment/Quota/QuotaResolver';
import useAccount from '@/compositions/accounting/useAccount';
import moment from 'moment';

export default {
  name: 'CandidatesTable',
  components: {
    Preloader,
    AppWithLoading,
    CandidateCard,
    PageTitle,
    IconClose,
    Tooltip,
    QuotaResolver,
  },
  props: {
    statuses: {
      type: Array,
      required: true,
    },
  },
  setup(props, { root: { $router, $route } }) {
    const { statuses } = toRefs(props);
    const {
      fetchAll: fetchAllCandidates, deleteAll, metaData, fetchCandidatesWithOrder, exportCandidates,
    } = useCandidates();
    const {
      fetchById: fetchVacancyById, findVacancyById, vacancies, fetch, copyCandidates,
    } = useVacancies();
    const { locale, t } = useI18n();
    const { quota } = useAccount();
    const { formatDate } = useDateFormatter();

    const filter = $route.query.filter ? JSON.parse($route.query.filter) : {};
    const page = ref($route.query.page || 1);
    const perPage = ref($route.query.perPage || 15);
    const order = ref($route.query.order || null);
    const orderBy = ref($route.query.orderBy || null);
    const search = ref($route.query.search || '');
    const selectedVacancyName = ref(filter.vacancyId || []);
    const selectedStatuses = ref(filter.status || []);
    const filtersStartDate = ref(filter.createdAtGt || '');
    const filtersEndDate = ref(filter.createdAtLt || '');
    const candidateSelectedId = ref(null);
    const vacancySelected = ref(null);
    const tableKey = ref(Date.now());
    const toggleChevron = ref(false);
    const sortOrder = ref(1);
    const activeSortHeaderValue = ref(null);
    const drawer = ref(null);
    const isInviteCandidates = ref(null);
    const panel = ref([0, 1]);
    const searchVacancy = ref(null);
    const selectedVacancyRadio = ref(null);
    const filtersActive = ref(false);
    const vacancyIdParsed = ref([]);
    const selectedAllUsers = ref(false);
    const selectedUsers = ref([]);
    const isToggledCheckboxUsers = false;
    const selectedStatus = ref(candidateStatus.INVITED);
    const withInvitation = ref(true);
    const candidateName = ref(null);
    const notifyBy = ref(candidateNotificationTypes.EMAIL);
    const notifyByEmail = ref(true);
    const notifyBySms = ref(false);
    const notifyByCall = ref(false);
    const tickets = ref([]);
    const ticketsMoreThanSMSes = computed(() => tickets.value.length > quota.value.sms);
    const ticketsMoreThanCalls = computed(() => tickets.value.length > quota.value.calls);
    const dateStartPickerActive = ref(false);
    const dateEndPickerActive = ref(false);
    const isMobile = ref(false);
    const isMobileSm = ref(false);
    const fetchedCandidateIds = ref([]);
    const loading = ref(false);

    const notifyTypes = [
      { value: candidateNotificationTypes.EMAIL, text: t('notifyByEmail'), quotaCode: quotaItemTypes.EMAIL },
      { value: candidateNotificationTypes.EMAIL_AND_SMS, text: t('notifyByEmailAndSMS'), quotaCode: quota.value.sms },
      { value: candidateNotificationTypes.EMAIL_AND_CALL, text: t('notifyByEmailAndCall'), quotaCode: quota.value.calls },
    ];

    const candidates = computed(() => fetchCandidatesWithOrder(
      orderBy.value,
      order.value,
      fetchedCandidateIds.value,
    ));

    const totalCandidates = computed(() => metaData?.value?.total);

    const searchVacancyHandler = computed(() => vacancies.value.filter((vacancy) => {
      return vacancy.title.toLowerCase().includes(searchVacancy.value || '');
    }));

    const phoneCount = computed(() => tickets.value.filter((ticket) => !!ticket.phone && ticket.phone !== '').length);
    const compareDate = computed(() => {
      const start = moment(filtersStartDate.value, 'DD-MM-YYYY').valueOf();
      const end = moment(filtersEndDate.value, 'DD-MM-YYYY').valueOf();
      return start > end;
    });

    const nothingSelected = computed(() => !selectedVacancyName.value.length && !filtersStartDate.value && !filtersEndDate.value
    && !selectedStatuses.value.length);

    const haveFilters = computed(() => search.value || filtersActive.value || orderBy.value);

    const dateFormat = computed(() => {
      switch (locale.value) {
        case 'ru': {
          return 'DD.MM.YYYY';
        }
        case 'en':
        default: {
          return 'MM/DD/YYYY';
        }
      }
    });

    function formattedCreatedAt(candidate) {
      switch (locale.value) {
        case 'ru': {
          return formatDate(candidate.createdAt, 'DD.MM.YYYY');
        }
        case 'en':
        default: {
          return formatDate(candidate.createdAt, 'MM/DD/YYYY');
        }
      }
    }

    function isQuotaExceededForRow(candidate) {
      return quota.value
        && Number(quota.value.interviews ?? 0) < 1
        && !candidate.quotaWrittenOff;
    }
    const titleInvitations = computed(() => {
      if (selectedUsers.value.length > 1) return `${t('invite')} ${selectedUsers.value.length} ${t('candidates')}`;
      return `${t('invite')} ${candidateName.value}`;
    });

    const localeLabelStart = computed(
      () => formatDate(new Date().setMonth(new Date().getMonth() - 1, 30), dateFormat.value),
    );
    const localeLabelEnd = computed(() => formatDate(new Date(), dateFormat.value));

    const filtersStartDateComputed = computed(() => {
      if (!filtersStartDate.value) {
        return null;
      }

      return formatDate(filtersStartDate.value, dateFormat.value);
    });

    const filtersEndDateComputed = computed(() => {
      if (!filtersEndDate.value) {
        return null;
      }

      return formatDate(filtersEndDate.value, dateFormat.value);
    });

    function setBackFormat(date) {
      if (date) {
        return moment(date, dateFormat.value).format('YYYY-MM-DD');
      }
      return null;
    }

    function setStartDate() {
      dateStartPickerActive.value = false;
      filtersStartDate.value = filtersStartDateComputed.value;
    }

    function setEndDate() {
      dateEndPickerActive.value = false;
      filtersEndDate.value = filtersEndDateComputed.value;
    }

    async function fetchVacancies() {
      await fetch();
    }

    async function fetchAll(params) {
      await deleteAll();
      const data = await fetchAllCandidates(params);
      tableKey.value = Date.now();
      return data;
    }

    async function fetchCandidatesWithFilters() {
      page.value = Number(page.value);
      if (isMobile.value) perPage.value = 8;
      if (isMobileSm.value) perPage.value = 15;
      const params = {
        search: search.value,
        page: page.value,
        perPage: perPage.value,
        orderBy: orderBy.value,
        order: order.value,
        filter: {
          createdAtGt: setBackFormat(filtersStartDate.value),
          createdAtLt: setBackFormat(filtersEndDate.value),
          vacancyId: selectedVacancyName.value,
          status: selectedStatuses.value,
        },
      };
      const data = await fetchAll(params);
      fetchedCandidateIds.value = (data || []).map((item) => item.id);
    }

    async function fetchCandidatesWithQueryParams() {
      const defaultPerPage = 15;

      const rawFilter = {
        createdAtGt: filtersStartDate.value,
        createdAtLt: filtersEndDate.value,
        vacancyId: selectedVacancyName.value,
        status: selectedStatuses.value,
      };

      const filterForQuery = Object.fromEntries(
        Object.entries(rawFilter).filter(([, value]) => {
          if (value === null || value === undefined) return false;
          if (typeof value === 'string') return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          return true;
        }),
      );

      const query = {
        ...(Number(page.value) !== 1 ? { page: page.value } : {}),
        ...(Number(perPage.value) !== defaultPerPage ? { perPage: perPage.value } : {}),
        ...(search.value?.trim?.() ? { search: search.value } : {}),
        ...(orderBy.value ? { orderBy: orderBy.value } : {}),
        ...(order.value ? { order: order.value } : {}),
        ...(Object.keys(filterForQuery).length ? { filter: JSON.stringify(filterForQuery) } : {}),
      };

      await $router.push({
        path: '/candidates',
        query,
      });
      await fetchCandidatesWithFilters();
    }

    async function fetchCandidatesWithQueryParamsWithLoading() {
      loading.value = true;
      await fetchCandidatesWithQueryParams();
      loading.value = false;
    }

    async function loadPage(newPage) {
      loading.value = true;
      await fetchAll({
        page: newPage,
      });
      page.value = newPage;
      loading.value = false;
    }

    async function redirectToVacancy(vacancyId) {
      await $router.push({ path: `vacancy/${vacancyId}` });
    }

    async function openCandidate(candidateId, vacancyId) {
      await fetchVacancyById(vacancyId);
      vacancySelected.value = findVacancyById(vacancyId);
      candidateSelectedId.value = candidateId;
    }

    function closeResume() {
      vacancySelected.value = null;
      candidateSelectedId.value = null;
    }

    const headers = [
      {
        text: t('name'), value: 'cName', sortable: true, isToggled: false, color: false,
      },
      {
        text: t('files'), value: 'cMaterials', sortable: false,
      },
      {
        text: t('email'), value: 'cEmail', sortable: false,
      },
      {
        text: t('phone'), value: 'cPhone', sortable: false,
      },
      {
        text: t('dateCreated'), value: 'cCreated', sortable: true, isToggled: false, color: false,
      },
      {
        text: t('vacancy'), value: 'cVacancy', sortable: true, isToggled: false, color: false,
      },
      {
        text: t('status'), value: 'cStatus', sortable: true, isToggled: false, color: false,
      },
      {
        text: '', value: 'cInvite', sortable: false,
      },
    ];
    const selected = ref([]);

    function updateSelected({ selected: isCandidateSelected, id }) {
      if (isCandidateSelected) {
        selected.value.push(id);
      } else {
        selected.value = selected.value.filter((i) => i !== id);
      }
    }

    const timer = ref(null);

    async function doSearch() {
      if (timer) {
        clearTimeout(timer.value);
        timer.value = null;
      }

      timer.value = setTimeout(async () => {
        page.value = 1;
        await fetchCandidatesWithQueryParamsWithLoading();
      }, 750);

      return true;
    }

    function onSortHeaderClick(header, event) {
      if (!header.sortable) {
        return;
      }
      if (event.target.closest('.th-wrap__checkboxes')) {
        return;
      }
      sortThis(header);
    }

    async function sortThis(header) {
      const isSwitchingColumn = activeSortHeaderValue.value !== header.value;
      if (isSwitchingColumn) {
        headers.forEach((h) => {
          h.color = false;
          h.isToggled = false;
        });
        sortOrder.value = 1;
        order.value = null;
        orderBy.value = null;
        activeSortHeaderValue.value = header.value;
      }

      switch (header.value) {
        case 'cName':
          orderBy.value = 'fullName';
          break;
        case 'cCreated':
          orderBy.value = 'createdAt';
          break;
        case 'cVacancy':
          orderBy.value = 'vacancyName';
          break;
        case 'cStatus':
          orderBy.value = 'status';
          break;
        default:
          orderBy.value = null;
      }

      sortOrder.value -= 1;

      page.value = 1;

      if (sortOrder.value === 0) {
        order.value = 'asc';
        header.color = true;
      }

      if (sortOrder.value === -1) {
        order.value = 'desc';
        header.isToggled = true;
      }

      if (sortOrder.value === -2) {
        order.value = null;
        orderBy.value = null;
        sortOrder.value = 1;
        header.isToggled = false;
        header.color = false;
        activeSortHeaderValue.value = null;
      }
      await fetchCandidatesWithQueryParamsWithLoading();
    }

    function downloadResume(candidate) {
      window.open(candidate.resume.file.url);
    }

    async function changePage(currentPage) {
      page.value = currentPage;
      await fetchCandidatesWithQueryParamsWithLoading();
    }

    async function changePagePrev() {
      page.value -= 1;
      await fetchCandidatesWithQueryParamsWithLoading();
    }

    async function changePageNext() {
      page.value += 1;
      await fetchCandidatesWithQueryParamsWithLoading();
    }

    async function showResultsFilters() {
      drawer.value = false;
      page.value = 1;
      filtersActive.value = true;
      await fetchCandidatesWithQueryParamsWithLoading();
    }

    watch(nothingSelected, () => {
      document.addEventListener('keyup', (event) => {
        if (event.code === 'Enter' && !nothingSelected.value) {
          showResultsFilters();
        }
      });
    });

    async function resetFilters() {
      loading.value = true;

      page.value = 1;
      order.value = null;
      orderBy.value = null;
      filtersEndDate.value = '';
      filtersStartDate.value = '';
      selectedVacancyName.value = [];
      selectedStatuses.value = [];
      filtersActive.value = false;
      search.value = '';
      searchVacancy.value = '';
      selectedUsers.value = [];
      selectedAllUsers.value = false;
      notifyBy.value = candidateNotificationTypes.EMAIL;
      selectedStatus.value = candidateStatus.INVITED;
      selectedVacancyRadio.value = null;
      headers.forEach((elem) => {
        elem.color = false;
        elem.isToggled = false;
      });
      await $router.replace({ path: '/candidates', query: {} });
      await fetchCandidatesWithFilters();

      loading.value = false;
    }

    function selectUsers() {
      this.isToggledCheckboxUsers = !this.isToggledCheckboxUsers;
      selectedUsers.value = [];
      if (selectedAllUsers.value) {
        candidates.value.map((candidate) => selectedUsers.value.push(candidate.id));
      }
    }

    function setNotifyTypeValue() {
      switch (notifyBy.value) {
        case candidateNotificationTypes.EMAIL_AND_SMS: {
          notifyByCall.value = false;
          notifyBySms.value = true;
          break;
        }
        case candidateNotificationTypes.EMAIL_AND_CALL: {
          notifyBySms.value = false;
          notifyByCall.value = true;
          break;
        }
        default: {
          notifyByEmail.value = true;
          notifyBySms.value = false;
          notifyByCall.value = false;
        }
      }
    }

    async function setCandidateName(candidate) {
      await fetchVacancies();
      selectedUsers.value = [candidate.id];
      selectedAllUsers.value = false;
      candidateName.value = `${candidate.firstName} ${candidate.lastName}`;
      isInviteCandidates.value = true;
    }

    async function inviteCandidate() {
      vacancyIdParsed.value = [];
      isInviteCandidates.value = false;
      await copyCandidates(selectedVacancyRadio.value, {
        status: selectedStatus.value,
        candidateIds: selectedUsers.value,
        notifyByEmail: notifyByEmail.value,
        notifyBySms: notifyBySms.value,
        notifyByCall: notifyByCall.value,
      });

      candidates.value.map((item) => {
        if (selectedUsers.value.includes(item.id)) {
          vacancyIdParsed.value.push(item.vacancyId);
        }
      });

      const allCandidatesAlreadyInvited = vacancyIdParsed.value.filter((x) => x === selectedVacancyRadio.value).length === vacancyIdParsed.value.length;

      if (allCandidatesAlreadyInvited) return this.$toast.error(this.$t('candidateCopied'));

      return this.$toast.success(this.$t('invited'));
    }

    function getQuotaCodeByNotify() {
      switch (notifyBy.value) {
        case candidateNotificationTypes.SMS: {
          return quotaItemTypes.SMS;
        }
        case candidateNotificationTypes.CALL: {
          return quotaItemTypes.CALLS;
        }
        case candidateNotificationTypes.EMAIL: {
          return quotaItemTypes.EMAIL;
        }
        default: {
          return '';
        }
      }
    }
    function formatDateOnChange() {
      switch (locale.value) {
        case 'ru': {
          if (filtersStartDate.value.length === 2 || filtersStartDate.value.length === 5) {
            filtersStartDate.value += '.';
          }
          if (filtersEndDate.value.length === 2 || filtersEndDate.value.length === 5) {
            filtersEndDate.value += '.';
          }
          break;
        }
        default: {
          if (filtersStartDate.value.length === 2 || filtersStartDate.value.length === 5) {
            filtersStartDate.value += '/';
          }
          if (filtersEndDate.value.length === 2 || filtersEndDate.value.length === 5) {
            filtersEndDate.value += '/';
          }
        }
      }
    }

    function openFilters() {
      drawer.value = !drawer.value;
    }

    function openInviteMenu() {
      isInviteCandidates.value = true;
      fetchVacancies();
    }

    function openStartCalendar() {
      dateStartPickerActive.value = true;
      dateEndPickerActive.value = false;
      filtersStartDate.value = null;
    }

    function openEndCalendar() {
      dateEndPickerActive.value = true;
      dateStartPickerActive.value = false;
      filtersEndDate.value = null;
    }

    function closeInvitations() {
      selectedUsers.value = [];
      selectedAllUsers.value = false;
    }

    function onResize() {
      isMobile.value = window.innerWidth <= 1180;
      isMobileSm.value = window.innerWidth <= 800;
    }

    window.addEventListener('resize', onResize);
    onResize();

    function hiddenHtml(item) {
      if (item) {
        document.getElementsByTagName('html')[0].style.overflow = 'hidden';
      } else {
        document.getElementsByTagName('html')[0].style.overflow = 'auto';
      }
    }

    function setCandidateStatus(candidate) {
      const candidateStatus = statuses.value.find((st) => st.status === candidate.status);
      if (!candidateStatus) {
        return t('statusNotFound');
      }

      return candidateStatus.name;
    }

    function setVisiblePage(currentPage) {
      return currentPage && (page.value - currentPage < 3 && page.value - currentPage > -3)
        || (currentPage === 1)
        || (currentPage === metaData.value.lastPage);
    }

    function setVisibleDots(currentPage) {
      return currentPage && page.value !== 4 && (page.value - currentPage === 3)
        || currentPage && page.value !== metaData.value.lastPage - 3 && (page.value - currentPage === -3);
    }

    const filtersSelected = computed(() => search.value?.length
      || order.value
      || orderBy.value
      || filtersStartDate.value?.length
      || filtersEndDate.value?.length
      || selectedVacancyName.value?.length
      || selectedStatuses.value?.length);

    async function downloadCandidatesFile(payload) {
      const response = await exportCandidates(payload);
      const fileName = `${t('exportCandidates')}.xlsx`;

      const url = URL.createObjectURL(new Blob([response.response.data], {
        type: 'application/vnd.ms-excel',
      }));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    }

    function exportCandidatesAll() {
      if (selectedUsers.value.length) {
        return downloadCandidatesFile({ candidateIds: selectedUsers.value });
      }

      if (filtersSelected.value) {
        return downloadCandidatesFile({
          mass: {
            search: search.value,
            orderBy: orderBy.value,
            order: order.value,
            filter: {
              createdAtGt: filtersStartDate.value,
              createdAtLt: filtersEndDate.value,
              vacancyId: selectedVacancyName.value,
              status: selectedStatuses.value,
            },
          },
        });
      }

      downloadCandidatesFile();
    }

    onMounted(async () => {
      await fetchVacancies();
    });

    watch(drawer, () => {
      hiddenHtml(drawer.value);
    });

    watch(isInviteCandidates, () => {
      hiddenHtml(isInviteCandidates.value);
    });

    return {
      setNotifyTypeValue,
      locale,
      setCandidateName,
      searchVacancyHandler,
      totalCandidates,
      resetFilters,
      sortThis,
      onSortHeaderClick,
      page,
      metaData,
      vacancySelected,
      candidateSelectedId,
      fetchVacancies,
      copyCandidates,
      loadPage,
      closeResume,
      openCandidate,
      headers,
      selected,
      updateSelected,
      search,
      orderBy,
      doSearch,
      timer,
      toggleChevron,
      formatDate,
      downloadResume,
      changePage,
      sortOrder,
      candidates,
      drawer,
      vacancies,
      panel,
      searchVacancy,
      showResultsFilters,
      filtersStartDate,
      filtersEndDate,
      selectedVacancyName,
      selectedStatuses,
      filtersActive,
      vacancyIdParsed,
      selectedAllUsers,
      selectedUsers,
      isToggledCheckboxUsers,
      selectUsers,
      isInviteCandidates,
      selectedStatus,
      withInvitation,
      inviteCandidate,
      candidateName,
      notifyBy,
      notifyByEmail,
      notifyBySms,
      notifyByCall,
      redirectToVacancy,
      notifyTypes,
      ticketsMoreThanSMSes,
      ticketsMoreThanCalls,
      getQuotaCodeByNotify,
      phoneCount,
      selectedVacancyRadio,
      dateStartPickerActive,
      dateEndPickerActive,
      nothingSelected,
      vacancyStatus,
      isMobile,
      formatDateOnChange,
      filtersStartDateComputed,
      filtersEndDateComputed,
      setStartDate,
      setEndDate,
      dateFormat,
      localeLabelStart,
      localeLabelEnd,
      filter,
      fetchCandidatesWithFilters,
      formattedCreatedAt,
      openFilters,
      openInviteMenu,
      closeInvitations,
      titleInvitations,
      isQuotaExceededForRow,
      hiddenHtml,
      openStartCalendar,
      openEndCalendar,
      compareDate,
      setCandidateStatus,
      changePageNext,
      changePagePrev,
      setVisiblePage,
      setVisibleDots,
      exportCandidatesAll,
      haveFilters,
      loading,
    };
  },
};
</script>

<style lang="scss" scoped>
.td-wrap__blur-wrapper {
  min-height: 1em;
}

.candidate-row__blurred {
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
}

.candidateActionButton {
  position: absolute;
  margin: 0 6px;
  top: 30%;
  @media screen and (max-width: 1180px) {
    margin: 0 14px;
  }
  &__resume {
    left: 30%;
    @media screen and (max-width: 1180px) {
      right: 40%;
    }
  }

  &__card {
    @media screen and (max-width: 1180px) {
      right: -13px;
    }
  }
}

table {
  width: 100%;
  table-layout: fixed;
  margin-bottom: 20px;
  border: 5px solid #fff;
  border-top: 5px solid #fff;
  border-bottom: 3px solid #fff;
  border-collapse: collapse;
  font-size: 15px;
  background: #fff!important;
}

thead {
  background: #f3f3f3;
  @media screen and (max-width: 1180px) {
    display: none;
  }
  tr {
    th:nth-child(2) {
      width: 130px;
      @media screen and (max-width: 1440px) {
        width: 80px;
      }
    }
    th:nth-child(4) {
      width: 150px;
      @media screen and (max-width: 1440px) {
        width: 135px;
      }
    }
    th:nth-child(5) {
      width: 130px;
      @media screen and (max-width: 1440px) {
        width: 100px;
      }
    }
    th:nth-child(6) {
      @media screen and (max-width: 1440px) {
        width: 160px;
      }
    }
    th:nth-child(8) {
      width: 130px;
      @media screen and (max-width: 1440px) {
        width: 115px;
      }
    }
  }
}
tr {
  height: 40px;
  border-bottom: 1px solid #f3f3f3;
}
th {
  text-align: left;
  padding: 0 16px;
  height: auto;
  transition: .1s;
  white-space: nowrap;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: #757575;
  &.th--sortable {
    padding: 0;
    cursor: pointer;
    user-select: none;
    &:hover {
      .th-wrap span,
      .th-wrap .default-color,
      .th-wrap .v-icon,
      .th-wrap i {
        color: rgba(0, 0, 0, .87);
        transition: .1s;
      }
    }
  }
  &:nth-child(2) {
    text-align: center;
    &:hover {
      color: #757575;
    }
  }
  &:nth-child(2), &:nth-child(3), &:nth-child(4) {
    cursor: default;
  }
}

tbody {
  @media screen and (max-width: 1180px) {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  tr {
    @media screen and (max-width: 1180px) {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 49%;
      background: #F3F3F3;
      border-radius: 15px;
      position: relative;
      padding: 15px 15px 15px 40px;
      &:hover {
        background: #F3F3F3;
      }
    }
    @media screen and (max-width: 800px) {
      width: 100%;
    }
    @media screen and (max-width: 380px) {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: #F3F3F3;
        box-shadow: 0px 10px 34px rgba(0, 0, 0, 0.05);
        border-radius: 15px;
        position: relative;
        padding: 10px 10px 10px 36px;
      }
    &:hover {
    background: #eee;
    }
  }
}

td {
  font-size: .875rem;
  padding: 0 0 0 16px;
  height: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media screen and (max-width: 1180px) {
    padding: 0;
  }
  a {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:nth-child(2) {
    padding-left: 0;
  }
  &:nth-child(8) {
    overflow: visible;
  }
}

.no-candidates {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 45%;
  font-weight: 400;
  font-size: 18px;
  line-height: 20px;
  color: #212121;
  &__open-filters {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #C9C9C9;
    margin-top: 7px;
    border-bottom: 1px dashed #C9C9C9;
    cursor: pointer;
  }
}

.candidates {
  &__title {
    font-weight: 400;
    font-size: 24px;
    line-height: 20px;
    color: #212121;
    margin: 0 0 30px;
    @media screen and (max-width: 599px) {
      margin: 30px 0 20px;
      font-size: 30px;
    }
  }
}

.v-tooltip::v-deep {
  color: red;
}

.v-input--checkbox::v-deep {
  padding: 0;
  margin: 0;
  .v-icon {
    color: #BBBBBB;
  }
  .v-messages {
    display: none;
  }
  .v-input__slot {
    margin-bottom: 0;
  }
}
.th-wrap {
  display: flex;
  align-items: center;
  &--sortable {
    width: 100%;
    min-height: 40px;
    padding: 0 16px;
    box-sizing: border-box;
    cursor: pointer;
  }
  span {
    margin-right: 6px;
    flex: 1;
    min-width: 0;
  }
  .v-icon {
    flex-shrink: 0;
  }
  &__checkboxes {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-right: 10px;
    pointer-events: none;
    > div {
      margin-top: -6px;
    }
    .v-input--checkbox,
    img {
      pointer-events: auto;
    }
    .v-input--checkbox::v-deep {
      flex: 0 0 auto;
      width: 24px;
      .v-input__control {
        width: 24px;
      }
      .v-input__slot {
        width: 24px;
        min-width: 24px;
      }
    }
  }
}

.td-wrap {
  &__name {
    display: flex;
    align-items: center;
    a {
      margin-top: 5px;
    }
    @media screen and (max-width: 1180px) {
      margin-bottom: 40px;
      white-space: normal;
      a {
        text-decoration: none;
        color: black;
        font-size: 20px;
        font-weight: 600;
      }
    }
    .v-input--checkbox::v-deep {
      @media screen and (max-width: 1180px) {
        position: absolute;
        left: 10px;
      }
      @media screen and (max-width: 380px) {
        position: absolute;
        left: 6px;
      }
    }
  }
  &__buttons {
    height: 40px;
    align-items: center;
    position: relative;
    text-align: center;
    @media screen and (max-width: 1180px) {
      position: absolute;
      right: 44px;
      bottom: 60px;
      height: auto;
      overflow: visible;
    }
    @media screen and (max-width: 380px) {
      left: 106px;
      bottom: 29px;
      right: auto;
    }
  }
  &__invite {
    @media screen and (max-width: 380px) {
      padding-bottom: 25px;
    }
    &_text {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      text-decoration-line: underline;
      color: #1F85D7;
      cursor: pointer;
    }
  }
  &__date {
    @media screen and (max-width: 1180px) {
      position: absolute;
      right: 15px;
      bottom: 15px;
      color: #9C9F9F;
    }
    @media screen and (max-width: 380px) {
      left: 10px;
      bottom: 10px;
    }
  }
  &__email {
    @media screen and (max-width: 1180px) {
      position: absolute;
      top: 70px;
    }
  }
  &__phone {
    @media screen and (max-width: 1180px) {
      position: absolute;
      top: 50px;
    }
  }
  &__vacancy {
    a {
      display: block;
        overflow: hidden;
    }
    @media screen and (max-width: 1180px) {
      margin-top: 10px;
      a {
        text-decoration: none;
        color: black;
        font-size: 14px;
      }
    }
  }
  &__status {
    @media screen and (max-width: 1180px) {
      color: #9C9F9F;
    }
  }
}

.candidates-filters {
  &__wrap {
    padding: 30px;
    height: 100%;
    overflow: scroll;
    padding-bottom: 150px;
    @media screen and (max-width: 1180px) {
      padding: 20px;
      padding-bottom: 200px;
    }
    @media screen and (max-width: 380px) {
      padding: 15px;
      padding-bottom: 200px;
    }
    &_header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      @media screen and (max-height: 625px) {
        margin-bottom: 20px;
      }
    }
    &_date-range {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 5px;
      hr {
        width: 30px;
        height: 1px;
        margin: -7px 10px 0 10px;
        background: #C9C9C9;
      }
    }
    &_date-range-error-message {
      color: #ff5252;
      font-size: 14px;
    }
    &_date-title {
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      color: #212121;
      margin-bottom: 20px;
    }
    &_status-title {
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      color: #212121;
      margin-bottom: 10px;
    }
    .v-select::v-deep {
      .v-input__control {
        .v-input__slot {
          border: 1px solid #E0E0E0;
          border-radius: 4px;
          padding: 0 12px;
          &::before {
            display: none;
          }
        }
      }
    }
  }
  &__title {
    font-size: 34px !important;
    font-weight: 400;
    line-height: 100%;
  }
}

.v-expansion-panels::v-deep {
  .v-expansion-panel {
    .v-expansion-panel-content {
      .v-expansion-panel-content__wrap {
        .candidates-table__textfield {
          margin-bottom: 10px;
        }
      }
    }
  }
}

.v-expansion-panel--active:not(:first-child), .v-expansion-panel--active + .v-expansion-panel {
  @media screen and (max-height: 625px) {
    margin-top: 0;
  }
}
.down {
  transform: rotate(0deg);
  color: rgba(0,0,0,.87);
}
 .up {
  transform: rotate(180deg);
  color: rgba(0,0,0,.87);
}

.black-color {
  color: rgba(0,0,0,.87);
}

.default-color {
  color: #757575;
}
.candidates-table {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    @media screen and (max-width: 1180px) {
      margin-top: 0 !important;
    }
  }
  &__filters-btn {
    margin-left: 8px;
    border: 1px solid #C9C9C9;
    padding: 6px 12px;
    border-radius: 9999px;
    cursor: pointer;
    @media screen and (max-width: 1180px) {
      position: absolute;
      right: 15px;
      top: 38px;
    }
    img {
      margin-top: 5px;
    }
  }
  &__filters-btn-active {
    margin-left: 8px;
    border: 1px solid #C9C9C9;
    padding: 6px 12px;
    border-radius: 9999px;
    cursor: pointer;
    position: relative;
    &::before {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      border-radius: 9999px;
      background-color: #1F85D7;
      right: 0;
      top: -3px;
      border: 3px solid #FFFFFF;
    }
    @media screen and (max-width: 1180px) {
      position: absolute;
      right: 15px;
      top: 38px;
    }
    img {
      margin-top: 5px;
    }
  }
  &__textfield {
    max-width: 500px;
    width: 500px;
    display: flex;
    align-items: center;
    position: relative;
    .v-input::v-deep {
      .v-input__control {
        .v-input__slot {
          margin-bottom: 0;
        }
      }
    }
    @media screen and (max-width: 1440px) {
      width: 400px;
    }
    @media screen and (max-width: 1180px) {
      width: 100%;
      max-width: 565px;
    }
    img {
      position: absolute;
      right: 15px;
      top: 12px;
      background-color: #fff;
      cursor: pointer;
    }
  }

  &__vacancies-list {
    height: fit-content;
    max-height: 160px;
    overflow-y: auto;
    overflow-x: hidden;
    @media screen and (max-height: 768px) {
      max-height: 122px;
    }
    @media screen and (max-height: 667px) {
      max-height: 100px;
    }

    .v-input--checkbox::v-deep {
      .v-input__control {
        .v-input__slot {
          label {
            color: #212121;
          }
        }
      }
    }
    @media screen and (max-width: 1180px) {
      max-height: 93px;
    }
  }

  &__filters-reset {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #C9C9C9;
    max-height: 20px;
    margin: 0 0 0 10px;
    border-bottom: 1px dashed #C9C9C9;
    cursor: pointer;
    @media screen and (max-width: 1180px) {
      display: none;
    }
  }
  &__filters-reset-mob {
    display: none;
    span {
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      color: #212121;
    }
    @media screen and (max-width: 1180px) {
      display: block;
    }
  }
  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__selected {
    background: #F3F3F3;
    border-radius: 4px;
    padding: 10px 20px;
    position: absolute;
    left: 40%;
    bottom: 10px;
    span {
      color: #212121;
    }
    button {
      margin: 0 20px;
    }
    @media screen and (max-width: 1180px) {
      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      bottom: 70px;
      background-color: #fff;
      border-radius: 10px;
      padding: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 280px;
      button {
        margin: 0 5px;
      }
    }
  }

  &__pagination {
    margin-left: 20px;
    display: flex;
    align-items: center;
    ul {
      display: flex;
      padding: 0;
      align-items: center;
      justify-content: start;
      margin: 0 7px;
    }
    li {
      display: flex;
      list-style: none;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      text-align: center;
      border-radius: 9999px;
      font-weight: 600;
      font-size: 14px;
      line-height: 100%;
    }
    &_arrow {
      cursor: pointer;
    }
  }
}

.v-input--radio-group::v-deep {
  .v-input__control {
    .v-input__slot {
      .v-input--radio-group__input {
        .v-radio {
          label {
            color: #212121;
          }
        }
      }
    }
  }
}
.isActivePage {
  background: #F3F3F3;
  color: #212121 !important;
}

.btn-show-block {
  background: #FFFFFF;
  box-shadow: 0px -4px 54px rgba(0, 0, 0, 0.1);
  position: absolute;
  display: flex;
  width: 100%;
  bottom: 0;
  left: 0;
  justify-content: center;
  min-height: 102px;
  align-items: center;
  padding: 20px 30px;
  z-index: 99;
  @media screen and (max-width: 1180px) {
    padding: 20px;
    min-height: 92px;
    gap: 10px;
    button {
      max-width: 50%;
    }
  }
}
.v-text-field::v-deep {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
        &::before {
        border: 1px solid #C9C9C9;
        border-radius: 4px;
      }
    }
  }
  .v-text-field__details {
    display: none;
  }
  .v-text-field__slot {
    label {
      top: 11px;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #9C9F9F;
      @media screen and (max-width: 320px) {
        font-size: 12px;
      }
    }
    input {
      @media screen and (max-width: 320px) {
        font-size: 12px;
      }
    }
  }
}

.v-input--checkbox::v-deep {
  padding-top: 7px;
  .v-icon {
    color: #757575;
  }
  .v-messages {
    display: none;
  }
  .v-input__slot {
    margin-bottom: 0;
  }
}

.v-expansion-panel::v-deep {
  &::before {
    box-shadow: none;
  }
  &::after {
    display: none;
  }
}
.v-expansion-panel-header::v-deep {
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #212121;
  padding: 0;
  min-height: 40px;
}
.v-expansion-panel-content::v-deep {
  > div {
    padding: 0;
  }
}

.v-navigation-drawer::v-deep {
  position: fixed !important;
  @media screen and (max-width: 599px) {
    width: 100% !important;
  }
}

.notify {
  border: none;
  padding: 0;
  &__item {
    margin-bottom: 8px;
  }
  &__rest-hint {
    display: none;
    position: absolute;
    white-space: nowrap;
    cursor: default;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 5px;
    top: 0;
    transform: translateY(calc(-100% - 15px))  translateX(-50%);
    left: 50%;
    background: #e0e0e0;
    padding: 5px 10px;

    &:after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      width: 15px;
      height: 15px;
      border-radius: 1px;
      transform: rotate(45deg) translate(-80%, 0%);
      background: inherit;
      z-index: -1;
    }
  }
  &__rest-amount {
    position: relative;
    margin-left: 5px;
    &:hover {
      .notify__rest-hint {
        display: block;
      }
    }
  }
}

.v-input--radio-group::v-deep {
  .v-input__control {
    .v-messages {
      display: none;
    }
    .v-input__slot {
      margin-bottom: 0;
      .v-input--radio-group__input {
        flex-flow: column;
        .v-radio {
          margin-left: 0;
        }
      }
    }
  }
}

.v-picker::v-deep {
  -webkit-box-shadow: 0px 15px 30px 9px rgba(34, 60, 80, 0.2);
  -moz-box-shadow: 0px 15px 30px 9px rgba(34, 60, 80, 0.2);
  box-shadow: 0px 15px 30px 9px rgba(34, 60, 80, 0.2);
  position: absolute;
  z-index: 999;
  max-width: 320px;
  height: 300px;
  top: 185px !important;
  @media screen and (max-width: 380px) {
    max-width: 275px;
  }
  @media screen and (max-width: 320px) {
    left: 15px !important;
    max-width: 275px;
  }
  .v-picker__body {
    width: 100% !important;
    .v-date-picker-header {
      margin-top: 18px;
      padding: 4px 10px;

      button {
        width: auto;
      }

      .v-date-picker-header__value {
        .accent--text {
          button {
            padding: 0;
            white-space: nowrap;
          }
        }
      }
    }
  }
  .v-picker__actions {
    button {
      position: absolute;
      top: 5px;
      right: 5px;
    }
  }
}

.date-picker {
  &__start {
    left: 29px;
    @media screen and (max-width: 1180px) {
      left: 20px;
    }
    @media screen and (max-width: 380px) {
      left: 15px;
    }
  }
  &__end {
    right: 45px;
    @media screen and (max-width: 1180px) {
      right: 35px;
    }
    @media screen and (max-width: 380px) {
      right: 29px;
    }
  }
}
.invite-candidate-icon {
  position: absolute;
  left: 15px;
  bottom: 18px;
  @media screen and (max-width: 380px) {
    position: absolute;
    left: 10px;
    bottom: 37px;
  }
}

::v-deep {
  .v-overlay {
    position: fixed !important;
  }
}
::v-deep .v-list-item__title {
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
}
::v-deep .v-list-item {
  min-height: auto;
}
::v-deep .v-list-item__content {
  padding: 6px 0;
}

::v-deep .v-input--selection-controls__ripple {
  display: none;
}

.paginationItem {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  cursor: pointer;
  text-align: center;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: #757575;
}

.loading-container {
  height: 100%;
}

.main-block {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap !important;
  height: 90%;
}
</style>

<i18n locale="en" lang="yaml">
imported: Imported
invited: Invited
interviewed: Passed the interview
inoffice_interview: Invited for an in-office interview
job_offer: Job offer
hired: Hired
declined: Declined
noCandidates: Not found
name: Candidates
email: Email
phone: Phone number
dateCreated: Date
vacancy: Job
status: Status
search: Search
files: Files
filters: Filters
showResults: Show
filtersReset: Reset the filter
dateAddCandidate: Date the candidate was added
openFilters: Open filters
invite: Invite
selected: Selected
inviteForAJob: Invite for a job
selectVacancy: Select job
selectStatus: Select status
inviteToVirtualInterview: Invite to a virtual interview
candidates: candidates
noVacancies: No jobs
notifyByEmail: Email
notifyByEmailAndSMS: Email + SMS
notifyByEmailAndCall: Email + Robotcall
notifyBySMS: Notify by text message
notifyByCall: Notify by automated call
notifyByEmailAndSMSTooltip: '{count} SMS left | {count} SMS left | {count} SMS left'
notifyByEmailAndCallTooltip: '{count} calls left | {count} call left | {count} calls left'
copyCandidateTooltip: Invite for another job
changeTariff: Change tariff
cancel: Cancel
ok: Ok
candidateCopied: The candidate has already been invited to this job
dateRangeErrorMessage: Please enter a valid date
statusNotFound: Status not found
exportCandidates: Export candidates
</i18n>

<i18n locale="ru" lang="yaml">
imported: Импортирован
invited: Приглашение отправлено
interviewed: Прошел интервью
inoffice_interview: Приглашен на очное интервью
job_offer: Предложение о работе
hired: Выход на работу
declined: Отказ
noCandidates: Не найдено
name: Кандидаты
email: Email
phone: Номер телефона
dateCreated: Дата
vacancy: Вакансия
status: Статус
search: Поиск
files: Файлы
filters: Фильтры
showResults: Показать
filtersReset: Сбросить фильтры
openFilters: Открыть фильтры
dateAddCandidate: Дата добавления кандидата
invite: Пригласить
selected: Выбрано
inviteForAJob: Пригласить на вакансию
selectVacancy: Выберите вакансию
selectStatus: Выберите статус
inviteToVirtualInterview: Пригласить на автоматическое интервью
candidates: кандидатов
noVacancies: Нет вакансий
notifyByEmail: Email
notifyByEmailAndSMS: Email + SMS
notifyByEmailAndCall: Email + Автоматический звонок
notifyBySMS: Оповестить по SMS
notifyByCall: Оповестить звонком автоинформатора
notifyByEmailAndSMSTooltip: 'Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS | Осталось {count} SMS'
notifyByEmailAndCallTooltip: 'Осталось {count} звонков | Остался {count} звонок | Осталось {count} звонка | Осталось {count} звонков'
copyCandidateTooltip: Пригласить на другую вакансию
changeTariff: Сменить тариф
cancel: Отменить
ok: Ок
candidateCopied: Кандидат уже приглашен на эту вакансию
dateRangeErrorMessage: Введите корректную дату
statusNotFound: Статус не найден
exportCandidates: Выгрузка кандидатов
</i18n>
